import { NavLink } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  FileText,
  Building2,
  TrendingUp,
  Receipt,
  Calculator,
  Settings,
  LogOut,
  Wallet,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  FileCheck,
} from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { useTaxStore } from '@/store/taxStore';

const navigation = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'Smart Import', href: '/smart-import', icon: Sparkles },
  { name: 'Documents', href: '/documents', icon: FileText },
  { name: 'Businesses', href: '/businesses', icon: Building2 },
  { name: 'Investments', href: '/investments', icon: TrendingUp },
  { name: 'Deductions', href: '/deductions', icon: Receipt },
  { name: 'Tasks', href: '/tasks', icon: ClipboardList },
  { name: 'Tax Summary', href: '/tax-summary', icon: Calculator },
  { name: 'Filing Guide', href: '/filing-guide', icon: FileCheck },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const { user, logout } = useAuthStore();
  const { selectedYear, setSelectedYear } = useTaxStore();

  const years = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear; y >= currentYear - 5; y--) {
    years.push(y);
  }

  return (
    <div
      className={cn(
        'fixed inset-y-0 left-0 bg-card border-r border-border flex flex-col transition-all duration-300 ease-in-out z-40',
        collapsed ? 'w-16' : 'w-64'
      )}
    >
      {/* Logo */}
      <div className="h-16 flex items-center px-4 border-b border-border">
        <Wallet className="h-8 w-8 text-primary flex-shrink-0" />
        {!collapsed && (
          <span className="ml-3 text-xl font-bold gradient-text whitespace-nowrap overflow-hidden">
            TaxVault
          </span>
        )}
      </div>

      {/* Year Selector */}
      <div className={cn('p-4 border-b border-border', collapsed && 'px-2')}>
        {!collapsed && (
          <label className="text-xs text-muted-foreground uppercase tracking-wider">
            Tax Year
          </label>
        )}
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          className={cn(
            'mt-2 w-full bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary',
            collapsed ? 'px-1 py-2 text-center' : 'px-3 py-2'
          )}
          title={collapsed ? `Tax Year: ${selectedYear}` : undefined}
        >
          {years.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
        {navigation.map((item) => (
          <NavLink
            key={item.name}
            to={item.href}
            title={collapsed ? item.name : undefined}
            className={({ isActive }) =>
              cn(
                'flex items-center py-2.5 text-sm font-medium rounded-lg transition-all duration-200',
                collapsed ? 'justify-center px-2' : 'px-3',
                isActive
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground'
              )
            }
          >
            <item.icon className={cn('h-5 w-5 flex-shrink-0', !collapsed && 'mr-3')} />
            {!collapsed && <span className="truncate">{item.name}</span>}
          </NavLink>
        ))}
      </nav>

      {/* User & Settings */}
      <div className="p-2 border-t border-border space-y-1">
        <NavLink
          to="/settings"
          title={collapsed ? 'Settings' : undefined}
          className={({ isActive }) =>
            cn(
              'flex items-center py-2.5 text-sm font-medium rounded-lg transition-all duration-200',
              collapsed ? 'justify-center px-2' : 'px-3',
              isActive
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground hover:bg-accent hover:text-foreground'
            )
          }
        >
          <Settings className={cn('h-5 w-5 flex-shrink-0', !collapsed && 'mr-3')} />
          {!collapsed && 'Settings'}
        </NavLink>
        <button
          onClick={() => logout()}
          title={collapsed ? 'Sign Out' : undefined}
          className={cn(
            'w-full flex items-center py-2.5 text-sm font-medium rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-all duration-200',
            collapsed ? 'justify-center px-2' : 'px-3'
          )}
        >
          <LogOut className={cn('h-5 w-5 flex-shrink-0', !collapsed && 'mr-3')} />
          {!collapsed && 'Sign Out'}
        </button>
      </div>

      {/* User Info */}
      <div className={cn('p-4 border-t border-border', collapsed && 'p-2')}>
        <div className={cn('flex items-center', collapsed && 'justify-center')}>
          <div
            className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0"
            title={collapsed ? user?.email : undefined}
          >
            <span className="text-sm font-medium text-primary">
              {user?.email?.[0]?.toUpperCase() || 'U'}
            </span>
          </div>
          {!collapsed && (
            <div className="ml-3 truncate min-w-0">
              <p className="text-sm font-medium truncate">{user?.name || 'User'}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
          )}
        </div>
      </div>

      {/* Collapse Toggle Button */}
      <button
        onClick={onToggle}
        className="absolute -right-3 top-20 h-6 w-6 bg-card border border-border rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent transition-colors shadow-sm"
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? (
          <ChevronRight className="h-4 w-4" />
        ) : (
          <ChevronLeft className="h-4 w-4" />
        )}
      </button>
    </div>
  );
}
